import {
  IconBarcode,
  IconBuildingWarehouse,
  IconCar,
  IconFileText,
  IconLayoutGrid,
  IconMapPin,
  IconPackage,
  IconStack,
  IconTruck,
  IconUser,
} from "@tabler/icons-react";

import { orpcClient } from "@/lib/orpc-client";
import { single } from "@/lib/orpc-batch";
import { useData } from "@/utility/state/contextAPI";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import { useState } from "react";
import { useTranslation } from "react-i18next";

function InventoryDonePage() {
  const { t } = useTranslation();
  const [inventoryDoneData, setInventoryDoneData] = useState({
    company: localStorage.getItem("companyId"),
    name: "",
    remarks: "",
    deliveryPerson: "",
    licensePlateNumber: "",
    warehouseStaff: "",
    documentId: "",
  });
  const [productList, setProductList] = useState([]);
  const [warehouseMap, setWarehouseMap] = useState({});
  const [areaMap, setAreaMap] = useState({});
  const [employeeData, setEmployeeData] = useState([]);

  const { id } = useParams();
  const {
    modal: { contextOpenModal, contextCloseModal, contextSetModalContent },
  } = useData();
  const navigate = useNavigate();

  const {
    // company,
    name,
    remarks,
    deliveryPerson,
    licensePlateNumber,
    warehouseStaff,
    warehouseStaffId,
    documentId,
    // contact,
    // storage_locations,
  } = inventoryDoneData || {};

  const warehouseStaffName =
    warehouseStaff?.name ||
    employeeData.find((e) => e?.id === warehouseStaffId)?.name ||
    "";

  useEffect(() => {
    const fetchData = async () => {
      try {
        let result = await single(orpcClient.stockReceipt.read([{ id: id! }]));
        if (!result) return;
        setInventoryDoneData(result);

        setProductList(result?.stockReceiptProduct || []);

        const warehouses = await orpcClient.warehouse.list({});
        const areaToWarehouse = {};
        const areaToArea = {};
        await Promise.all(
          (warehouses ?? []).map(async (warehouse) => {
            const areas = await orpcClient.warehouseArea.list({
              filter: { warehouseId: warehouse?.id },
            });
            (areas ?? []).forEach((area) => {
              if (area?.id) {
                areaToWarehouse[area.id] = warehouse;
                areaToArea[area.id] = area;
              }
            });
          })
        );
        setWarehouseMap(areaToWarehouse);
        setAreaMap(areaToArea);

        const employees = await orpcClient.employee.list({});
        setEmployeeData(employees);
      } catch (error) {
        console.error("Error fetching stock receipt data:", error);
      }
    };

    fetchData();
  }, []);

  const renderField = (
    label: string,
    value: React.ReactNode,
    Icon?: React.ComponentType<{ size?: number | string; className?: string }>
  ) => (
    <div className="space-y-1.5">
      <label className="flex items-center gap-1.5 text-xs text-gray-500">
        {Icon ? <Icon size={13} /> : null}
        {label}
      </label>
      <div className="flex h-9 items-center rounded-md border border-gray-200 bg-gray-50 px-3 text-sm text-gray-700">
        {value || <span className="text-gray-400">—</span>}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen p-8">
      <div className="mx-auto max-w-3xl rounded-xl bg-white p-8 space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-4xl font-bold" style={{ color: "#6F6F6F" }}>
              {name || t("warehouse.stockReceiptName") || "入庫單"}
            </div>
            <div className="mt-2 text-sm text-gray-500">
              {documentId || t("warehouse.stockReceiptDoneManagement") || "入庫單"}
            </div>
          </div>
          {/* <button
            type="button"
            onClick={() => navigate(`/hrMainPage/editInventoryDone/${id}`)}
            className="cursor-pointer flex h-10 w-10 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 shadow-sm hover:bg-gray-50"
          >
            <IconPencil size={18} />
          </button> */}
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
            <IconFileText size={16} className="text-blue-600" />
            <h3 className="text-sm font-medium text-gray-700">
              {t("warehouse.basicInfo") || "基本資訊"}
            </h3>
          </div>
          <div className="grid grid-cols-1 gap-4 p-6 md:grid-cols-2">
            {renderField(
              t("warehouse.stockReceiptName") || "入庫單名稱",
              name,
              IconPackage
            )}
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
            <IconPackage size={16} className="text-blue-600" />
            <h3 className="text-sm font-medium text-gray-700">
              {t("warehouse.productList") || "商品清單"}
            </h3>
          </div>
          <div className="space-y-4 p-6">
            {productList?.length ? (
              productList.map((item, i) => (
                <div
                  className="rounded-lg border border-gray-200 p-4"
                  key={item?.id ?? i}
                >
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {/* {renderField(
                      t("warehouse.productCode") || "商品編號",
                      item?.product?.code,
                      IconBarcode
                    )} */}
                    {renderField(
                      t("warehouse.productName") || "商品名稱",
                      item?.product?.name,
                      IconPackage
                    )}
                    {renderField(
                      t("warehouse.warehouseLabel") || "倉庫",
                      warehouseMap[item?.storageLocation?.warehouseAreaId]
                        ?.name,
                      IconBuildingWarehouse
                    )}
                    {renderField(
                      t("warehouse.areaLabel") || "區",
                      areaMap[item?.storageLocation?.warehouseAreaId]?.name,
                      IconLayoutGrid
                    )}
                    {renderField(
                      t("warehouse.storageLocationLabel") || "儲位",
                      item?.storageLocation?.name,
                      IconMapPin
                    )}
                    {renderField(
                      t("warehouse.quantity") || "數量",
                      item?.amount,
                      IconStack
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="flex h-9 items-center rounded-md border border-gray-200 bg-gray-50 px-3 text-sm text-gray-400">
                —
              </div>
            )}
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
            <IconTruck size={16} className="text-blue-600" />
            <h3 className="text-sm font-medium text-gray-700">
              {t("warehouse.deliveryInfo") || "交貨資訊"}
            </h3>
          </div>
          <div className="grid grid-cols-1 gap-4 p-6 md:grid-cols-2">
            {renderField(
              t("warehouse.deliveryPerson") || "送貨人員",
              deliveryPerson,
              IconTruck
            )}
            {renderField(
              t("warehouse.licensePlateNumber") || "送貨車牌",
              licensePlateNumber,
              IconCar
            )}
            {renderField(
              t("warehouse.inboundStaff") || "入庫人員",
              warehouseStaffName,
              IconUser
            )}
            {renderField(
              t("warehouse.remarks") || "備註",
              remarks,
              IconFileText
            )}
          </div>
        </div>

        {/* <div className="flex justify-center">
          <Button
            variant="filled"
            color="gray"
            style={{ width: "100%", maxWidth: "250px" }}
            radius="md"
          >
            {t("warehouse.closeOrder") || "結案"}
          </Button>
        </div> */}
      </div>
    </div>
  );
}
export default InventoryDonePage;
